import { JOINTS } from './joints'
import type { HumanoidModel, PointSet } from './model'

/**
 * Gen5 — die Zahlenbasis aus „Gen5 Basics – Kinematik und Performance", Stand
 * V0_24 (29.10.2026). CAD-Bezug Gen5_V0_13 mit Bodypanels V3.
 *
 * Nur Daten. Wer einen neuen Auslegungsstand hat, legt ein zweites Objekt
 * daneben und ändert am Rechenkern nichts.
 *
 * RAHMENWECHSEL, EINMAL UND HIER. Der Kinematikstand misst im CAD-Rahmen:
 * Ursprung Oberkante Abtrieb Becken-Gier, x nach links, y nach hinten, z nach
 * oben. Das Modell misst vom Kardankreuz des Rumpfgelenks aus, x nach vorn,
 * y nach links, z nach oben. Die Umrechnung ist
 *
 *     x = −y_cad      y = x_cad      z = z_cad − 20
 *
 * (das Kardankreuz liegt im CAD bei (0, 40, 20)). Jede Zahl unten trägt ihren
 * CAD-Wert im Kommentar, damit man sie gegen den Stand nachschlagen kann.
 *
 * WAS DER STAND NICHT HERGIBT, steht als ANNAHME markiert und ist vom
 * Vorgängermodell übernommen: Fuss- und Kopfgeometrie, Trägheitsradien, Geh- und
 * Laufgeschwindigkeit, Rekuperation. Eine unmarkierte Zahl ist eine aus dem
 * Stand.
 */

/**
 * Gelenkpunkte Gen5, Millimeter, linke Seite (y > 0).
 *
 * AUF GANZE MILLIMETER GERUNDET. Der Stand gibt Zehntel an; die stehen im
 * Kommentar jedes Punktes. Der Achsbaum (`rig/rig.ts`) rechnet die Weltlage
 * als Summe von Differenzen und muss den Punktsatz bitgenau treffen — mit
 * Zehnteln geht das in Gleitkommazahlen nicht auf, mit ganzen Zahlen immer.
 * Ein halber Millimeter ändert an keiner Rechnung dieser Anwendung etwas.
 *
 * REIHENFOLGE DER HÜFTKETTE. Gen5 baut Nicken → Rollen → Gieren (B1 → B2 → B3),
 * das Modell führt die Kette fest als Gieren → Rollen → Nicken. Die Achspunkte
 * stehen an ihren CAD-Orten; die Kettenreihenfolge ist die des Rechenkerns.
 * Für Segmentlängen, Gang und Momente ist das ohne Belang — sie hängen an
 * Nickachse, Knie und Sprunggelenk —, für einen Export der Gelenkreihenfolge
 * nicht. Ebenso ist die Hüft-Nickachse im CAD um 25° geneigt
 * (0,906, −0,015, −0,422); das Modell kennt nur eine reine Querachse.
 */
export const GEN5_POINTS: PointSet = {
  /* T2/T3 Kardankreuz, CAD (0, 40, 20). */
  cross: { label: 'Kardankreuz Rumpfgelenk (T2/T3)', x: 0, y: 0, z: 0, fixed: true, dof: 'rumpf' },
  /* Bauchmotor-Achse, CAD (±40, −10, 65). Die Motoren sitzen im Oberkörper,
     die Stangen laufen nach unten auf den Taillenträger — umgekehrt zum Vorgängermodell. */
  waistD: { label: 'Bauchmotor · Kurbelachse', x: 10, y: 40, z: 45 },
  /* Unterer Stangenpunkt am Taillenträger, CAD (±42,3, −10, −16). */
  waistC: { label: 'Bauchstange · Anlenkpunkt Taillenträger', x: 10, y: 42, z: -36 },
  /* ANNAHME: Unterkante Zentralbox auf Höhe der Bauchmotor-Achsen. */
  chest: { label: 'Unterkante Oberkörper', x: 0, y: 0, z: 45 },
  /* T1 Taille-Gier, CAD (0, 0, −16,4). Der Beckenpunkt trägt im Modell keine
     Freiheit; der Motor dort steht in der Motorisierung als „Becken-Gier". */
  pelvis: { label: 'Taille-Gier (T1)', x: 0, y: 0, z: -36 },
  /* B3 Bein-Gier, CAD (111,4, 4,0, −207,3). */
  hipG: { label: 'Bein-Gier (B3)', x: -4, y: 111, z: -227, dof: 'hipG' },
  /* B2 Hüft-Rollen, CAD (140,0, −5,3, −151,9). */
  hipR: { label: 'Hüft-Rollen (B2)', x: 5, y: 140, z: -172, dof: 'hipR' },
  /* B1 Hüft-Nicken, CAD (87,8, −4,4, −125,9). */
  hipN: { label: 'Hüft-Nicken (B1)', x: 4, y: 88, z: -146, dof: 'hipN' },
  /* B5 Knie, CAD (131,1, 25,7, −493,3). */
  knee: { label: 'Knie (B5)', x: -26, y: 131, z: -513, dof: 'knee' },
  /* B7/B8 Kreuzgelenk Sprung, CAD (165,6, 15,7, −986,8). */
  ankle: { label: 'Sprunggelenk (B7/B8)', x: -16, y: 166, z: -1007, dof: 'ankle' },
  /* ANNAHME: kein Zehengelenk im Stand — Fussgeometrie wie Vorgängermodell, 105 mm vor
     dem Sprunggelenk, 12 mm über der Sohle. Der Fuss ist starr; siehe `dofs`. */
  mtp: { label: 'Zehengrundgelenk (starr)', x: 89, y: 166, z: -1060, dof: 'mtp' },
  /* Sohle in der Nullpose bei z ≈ −1052 (CAD); Sohlenpunkt unter dem Sprunggelenk. */
  sole: { label: 'Sohlenpunkt unter dem Sprunggelenk', x: -16, y: 166, z: -1072 },
  /* A1/A2 Schulter, CAD (205, 66, 390). */
  shoulder: { label: 'Schulter (A1/A2)', x: -66, y: 205, z: 370, dof: 'shoulder' },
  /* A4 Ellbogen, CAD (241, 79, 125). */
  elbow: { label: 'Ellbogen (A4)', x: -79, y: 241, z: 105, dof: 'elbow' },
  /* Handposition am Ende der Ersatzstange, CAD (277,5, 23, −133). Die Last
     sitzt 90 mm dahinter. Das einzige Gelenk darunter ist A5 (Unterarm-Drehen). */
  wrist: { label: 'Hand (Ende Ersatzstange)', x: -23, y: 278, z: -153, dof: 'wrist' },
  /* ANNAHME: Kopfmitte 240 mm über der Schulterachse, wie Vorgängermodell. */
  headC: { label: 'Kopfmitte', x: 0, y: 0, z: 610, dof: 'hals' },
}

export const GEN5: HumanoidModel = {
  id: 'gen5-v0-24',
  label: 'Gen5, Stand V0_24 (10/2026)',

  /* Die Punkte sind CAD-Masse; nichts wird nachskaliert. */
  scale: 1,

  /* Ferse bei y_cad +94, Sprunggelenk bei +16: 78 mm hinter dem Sohlenpunkt. */
  heelOffset: 0.078,

  points: GEN5_POINTS,

  /*
    Winkelfreiheiten im Roboterrahmen. x = Rollen, y = Nicken, z = Gieren.

    MOMENTE: die Spitze am Gelenk aus der Reservetabelle des Stands
    (Wirkungsgrad Getriebe 0,90, Stange 0,97). Winkelabhängige Antriebe
    (Knie, Ellbogen) tragen ihr Maximum; der Wert in gestreckter Lage steht
    im Kommentar.

    WINKELBEREICHE: die Grenzwinkeltabelle des Stands (Modellwerte, nicht der
    heutige CAD-Freigang — der ist an Rumpf-Nicken, Hüft-Rollen und
    Sprung-Rollen noch enger und steht als offener Punkt in der
    Motorisierung). Vorzeichen in die Zählung des Modells übersetzt: sagittal
    positiv = beugen (Hüfte, Knie, Ellbogen), nach vorn (Schulter) bzw.
    Dorsalflexion (Sprung — die Gangkurven zählen Plantarflexion negativ);
    frontal und transversal positiv = nach aussen.
  */
  dofs: {
    rumpf: {
      label: 'Rumpfgelenk (T2/T3)',
      /* Rollen 428 Nm, Nicken 387 Nm — 2× FMK07019 über die Bauchstangen.
         Grenzen: T3 ±10 (Kasten mit Nicken ±20), T2 −20 zurück / +20 vor. */
      x: { lo: -10, hi: 10, moment: 428 },
      y: { lo: -20, hi: 20, moment: 387 },
      /* T1 Taille-Gier (135 Nm) sitzt am Beckenpunkt — siehe oben. */
      z: null,
    },
    /* B3: −45 innen / +45 aussen. */
    hipG: { label: 'Bein-Gier (B3)', x: null, y: null, z: { lo: -45, hi: 45, moment: 135 } },
    /* B2: −40 abspreizen / +20 anziehen → nach aussen positiv: −20 … 40. */
    hipR: { label: 'Hüft-Rollen (B2)', x: { lo: -20, hi: 40, moment: 178 }, y: null, z: null },
    /* B1: −120 beugen / +30 strecken → beugen positiv: −30 … 120. */
    hipN: { label: 'Hüft-Nicken (B1)', x: null, y: { lo: -30, hi: 120, moment: 259 }, z: null },
    /* 156 Nm gestreckt, 346 Nm im Maximum der Kurbelschwinge (60–70°), bei 140°
       noch 45 % davon. Grenzen −2 Anschlag / +140 beugen. */
    knee: { label: 'Knie (B5)', x: null, y: { lo: -2, hi: 140, moment: 346 }, z: null },
    ankle: {
      label: 'Sprunggelenk (B7/B8)',
      /* Parallelkinematik: Rollen 221 Nm, Nicken 359 Nm. Grenzen im Kasten:
         B8 −20 Eversion / +15 Inversion → nach aussen (Eversion) positiv;
         B7 −30 Spitze hoch / +15 runter → Dorsalflexion positiv: −15 … 30.
         Die Plantarflexion ist damit knapp (der Stand sagt es selbst); der
         Sprint fordert mehr, und die Grenzlagenprüfung zeigt das. */
      x: { lo: -15, hi: 20, moment: 221 },
      y: { lo: -15, hi: 30, moment: 359 },
      z: null,
    },
    /* KEIN ZEHENGELENK. Der Gen5-Fuss ist starr; was am Vorfuss anfällt, trägt
       die Struktur. Eine Null hier wäre ein Antrieb mit null Moment — das
       Fehlen ist die richtige Auskunft. */
    mtp: { label: 'Zehengrundgelenk (starr)', x: null, y: null, z: null },
    shoulder: {
      label: 'Schulter (A1/A2/A3)',
      /* A2: −180 seitlich heben / +20 anziehen → nach aussen positiv. */
      x: { lo: -20, hi: 180, moment: 175 },
      /* A1: −180 vor/hoch / +60 zurück → nach vorn positiv. */
      y: { lo: -60, hi: 180, moment: 175 },
      /* A3 Oberarm-Drehen, FMK06018 i 26: 70 Nm, −90 innen / +90 aussen. */
      z: { lo: -90, hi: 90, moment: 70 },
    },
    /* Bizeps-Zugstange: 78 Nm gestreckt, 167 Nm bei 90°. A4: −130 beugen /
       +2 Anschlag → beugen positiv: −2 … 130. */
    elbow: { label: 'Ellbogen (A4)', x: null, y: { lo: -2, hi: 130, moment: 167 }, z: null },
    wrist: {
      label: 'Unterarm-Drehen (A5)',
      /* Kein Handgelenk im Stand — nur die Drehung um die Unterarmachse,
         A5 −90 Pronation / +90 Supination. */
      x: null,
      y: null,
      z: { lo: -90, hi: 90, moment: 19 },
    },
    /* Der Kopf sitzt fest auf dem Oberkörper. */
    hals: { label: 'Hals (starr)', x: null, y: null, z: null },
  },

  /*
    Einzelmassen in Kilogramm — aus dem Massenmodell 55 kg des Stands
    (CAD-Volumen × Dichte, Motoren mit Datenblattmasse, Akku als Ausgleich).

    DIE KNOCHEN DES STANDS SIND FEINER ALS DIE SEGMENTE DES MODELLS. Das Modell
    kennt sieben Segmente; der Stand führt vierzehn Knochen. Zusammengefasst
    wird entlang der Kette, nicht nach Namen:

      Oberschenkel  = Oberschenkel 4,56 + Hüft-Rollgehäuse 2,05 + Gierträger 0,31
                      (alles, was ab der Hüft-Nickachse mit dem Bein geht)
      Oberarm       = Schulterglied 1 1,33 + Schulterglied 2 0,96 + Oberarm 1,69
      Unterarm      = Unterarm proximal 0,88 + Unterarm mit Hand 0,99
      Kopf          = Kopf 1,5 (im Stand Teil des Oberkörpers)
      Rumpf         = Oberkörper 10,22 − Kopf 1,5 + Becken 9,22 + Taillenträger 0,29
                      + Kurbeln, Stangen, Kardankreuze als Rest auf 55 kg

    `com` ist der Schwerpunkt auf der Knochenlinie, aus den Schwerpunkten des
    Stands massengewichtet gerechnet. `gyration` bleibt ANNAHME (anthropometrisch,
    wie Vorgängermodell): der Stand nennt Trägheiten aus dem CAD, aber keine Trägheitsradien
    je Segment.
  */
  masses: {
    /* Schwerpunkte CAD: Rollgehäuse z −142, Gierträger −177, Oberschenkel −287;
       Nickachse −125,9, Knie −493,3 → gewichtet 0,31 der Länge. */
    thigh: { label: 'Oberschenkel', kg: 6.92, com: 0.31, gyration: 0.323, paired: true, length: null },
    /* Schwerpunkt z −676 zwischen Knie −493,3 und Sprung −986,8. */
    shank: { label: 'Unterschenkel', kg: 3.66, com: 0.37, gyration: 0.302, paired: true, length: null },
    /* ANNAHME Fusslänge wie Vorgängermodell. */
    foot: { label: 'Fuss', kg: 0.41, com: 0.56, gyration: 0.475, paired: true, length: 0.2508 },
    /* Schulterglied 1 an der Schulter, Schulterglied 2 bei z 327, Oberarm bei 238;
       Schulter 390, Ellbogen 125 → gewichtet 0,30. */
    upperArm: { label: 'Oberarm', kg: 3.98, com: 0.3, gyration: 0.322, paired: true, length: null },
    /* proximal z 75, Unterarm −122; Ellbogen 125, Hand −133 → 0,60. */
    forearm: { label: 'Unterarm', kg: 1.87, com: 0.6, gyration: 0.303, paired: true, length: null },
    /* Oberkörper-Schwerpunkt z 333, Becken −87 → gewichtet z 115, auf der
       Linie Taille-Gier (−16) → Schulter (390) bei 0,32. Länge: senkrechter
       Abstand Taille-Gier → Schulterachse. */
    trunk: { label: 'Rumpf', kg: 19.82, com: 0.32, gyration: 0.5, paired: false, length: 0.406 },
    /* ANNAHME Kopflänge wie Vorgängermodell. */
    head: { label: 'Kopf', kg: 1.5, com: 0, gyration: 0.495, paired: false, length: 0.24035 },
    /* Auslegung der Beine mit 70 kg: 55 + 15 kg Nutzlast. */
    payload: { label: 'Nutzlast', kg: 15, com: 0, gyration: 0.289, paired: false, length: 0.8 },
  },

  massTarget: 55,

  /* Laufen bis 4 m/s ist die Auslegungsgrenze des Stands. Gehen, Rennen und
     Kadenz: ANNAHME wie Vorgängermodell. */
  performance: {
    walkMax: 1.6,
    runMax: 3.0,
    sprintMax: 4.0,
    cadenceMax: 240,
  },

  /* ANNAHME: Rekuperationsparameter des Vorgängermodells — der Stand sagt
     nichts zur Rekuperation. Gesamtwirkungsgrad der Feder rund 44 %;
     150 Nm am Gelenk ≈ 27 W Kupferverlust. */
  recuperation: {
    beta: 0.55,
    eta: 0.8,
    cap: 60,
    tau: 0.5,
    gen: 0.85,
    inv: 0.85,
    copper: 0.0012,
    idle: 60,
  },

  joints: JOINTS,
}
